// Kapazitäts-Speicherung.
//
// Datenform (drei-stufig):
//   capacityByTeam[team][release][monthKey] = CellValue
//
// CellValue ist entweder
//   { s: number; b: number }     — Stories und Bugs separat (aktuell)
//   number                        — Legacy aus V1-Migrationen (= s+b, hälftig)
//
// Wir behandeln beide Formen in cellOf() einheitlich, sodass die UI mit dem
// neuen Format arbeitet und alte Daten transparent gelesen werden können.

export type CellValue = number | { s: number; b: number };
export type CapMap   = Record<string, Record<string, Record<string, CellValue>>>;

export function cellOf(val: CellValue | undefined, kind?: 's' | 'b'): number {
  if (val == null) return 0;
  if (typeof val === 'number') {
    if (kind === 's' || kind === 'b') return val / 2;
    return val;
  }
  if (kind === 's') return val.s || 0;
  if (kind === 'b') return val.b || 0;
  return (val.s || 0) + (val.b || 0);
}

type CapTotalOpts = {
  team?: string;        // wenn gesetzt: nur dieses Team
  onlyRelease?: string; // wenn gesetzt (nur bei team-mode): einschränken auf Release
};

// Summe der Kapazität für einen Release (oder bei team-mode: für ein Team).
//
//   capTotalForRelease(cap, '2026 R2')                       — alle Teams
//   capTotalForRelease(cap, '2026 R2', 's', { team: 'Door' })— nur Team Door
//   capTotalForRelease(cap, 'Door', undefined, { team: 'Door', onlyRelease: '2026 R2' })
//                                                            — Team-Block mit Release-Filter
//
// Die "Door"-Schreibweise nutzt die V2-API: blockName ist ein Team-Name
// bei groupMode='team'; sonst ist es der Release-Name.
export function capTotalForRelease(
  cap: CapMap,
  releaseName: string,
  kind?: 's' | 'b',
  opts: CapTotalOpts = {}
): number {
  const { team, onlyRelease } = opts;

  // groupMode='team': releaseName = teamName
  if (team && team === releaseName) {
    let total = 0;
    const teamCap = cap[releaseName] ?? {};
    if (onlyRelease) {
      const months = teamCap[onlyRelease] ?? {};
      for (const v of Object.values(months)) total += cellOf(v, kind);
    } else {
      for (const releaseMap of Object.values(teamCap)) {
        for (const v of Object.values(releaseMap)) total += cellOf(v, kind);
      }
    }
    return total;
  }

  // Standard: releaseName ist ein Release-Name
  if (team) {
    const months = cap[team]?.[releaseName] ?? {};
    let total = 0;
    for (const v of Object.values(months)) total += cellOf(v, kind);
    return total;
  }
  let total = 0;
  for (const teamCap of Object.values(cap)) {
    const months = teamCap?.[releaseName] ?? {};
    for (const v of Object.values(months)) total += cellOf(v, kind);
  }
  return total;
}
